import { useEffect } from 'react'
import { Trash2, ChevronUp, ChevronDown, GripVertical, AlertCircle } from 'lucide-react'
import { BENEFIT_FIELDS, RULE_ACTIONS, RULE_OPERATORS } from '../../data/rulesEngineDefaults'
import type { BenefitRule, RuleAction } from '../../types/rulesEngine'
import type {
  BenefitRuleActionMasterRecord,
  BenefitRuleFieldMasterRecord,
  BenefitRuleFieldValueMasterRecord,
  BenefitRuleOperatorMasterRecord,
} from '../../services/benefitRuleMastersService'
import { useTheme } from '../../contexts/ThemeContext'
import Input from '../forms/Input'
import Select from '../forms/Select'

type RuleEditorRowProps = {
  rule: BenefitRule
  index: number
  totalRules: number
  fields: BenefitRuleFieldMasterRecord[]
  operators: BenefitRuleOperatorMasterRecord[]
  actions: BenefitRuleActionMasterRecord[]
  fieldValuesByFieldId: Record<string, BenefitRuleFieldValueMasterRecord[]>
  onLoadFieldValues: (fieldMasterId: string) => Promise<BenefitRuleFieldValueMasterRecord[]>
  useApiMasters: boolean
  onUpdate: (rule: BenefitRule) => void
  onDelete: () => void
  onMove: (index: number, direction: -1 | 1) => void
}

const ACTION_ACCENT: Record<RuleAction, { color: string; bg: string; border: string }> = {
  continue: { color: '#2563eb', bg: '#eff6ff', border: '#bfdbfe' },
  approve: { color: '#0d9488', bg: '#f0fdfa', border: '#99f6e4' },
  deny: { color: '#dc2626', bg: '#fef2f2', border: '#fecaca' },
  route_prior_auth: { color: '#d97706', bg: '#fffbeb', border: '#fde68a' },
  flag: { color: '#2563eb', bg: '#eff6ff', border: '#bfdbfe' },
}

function resolveFieldMasterId(
  rule: BenefitRule,
  fields: BenefitRuleFieldMasterRecord[]
): string {
  if (rule.conditionFieldMasterId) return rule.conditionFieldMasterId
  return fields.find((f) => f.value_key === rule.field)?.id ?? rule.field
}

function resolveOperatorMasterId(
  rule: BenefitRule,
  operators: BenefitRuleOperatorMasterRecord[]
): string {
  if (rule.conditionOperatorMasterId) return rule.conditionOperatorMasterId
  return operators.find((o) => o.value_key === rule.operator)?.id ?? rule.operator
}

function resolveActionMasterId(
  rule: BenefitRule,
  actions: BenefitRuleActionMasterRecord[],
  kind: 'pass' | 'fail'
): string {
  if (kind === 'pass') {
    if (rule.onPassActionMasterId) return rule.onPassActionMasterId
    return actions.find((a) => a.value_key === rule.action)?.id ?? rule.action
  }
  if (rule.onFailActionMasterId) return rule.onFailActionMasterId
  return actions.find((a) => a.value_key === rule.failAction)?.id ?? rule.failAction
}

function resolveValueMasterId(
  rule: BenefitRule,
  fieldValues: BenefitRuleFieldValueMasterRecord[]
): string {
  if (rule.conditionValueMasterId) return rule.conditionValueMasterId
  return fieldValues.find((v) => v.value_key === rule.value)?.id ?? rule.value
}

export default function RuleEditorRow({
  rule,
  index,
  totalRules,
  fields,
  operators,
  actions,
  fieldValuesByFieldId,
  onLoadFieldValues,
  useApiMasters,
  onUpdate,
  onDelete,
  onMove,
}: RuleEditorRowProps) {
  const { theme } = useTheme()

  const fieldMasterId = useApiMasters ? resolveFieldMasterId(rule, fields) : ''
  const fieldValues = fieldMasterId ? fieldValuesByFieldId[fieldMasterId] ?? [] : []

  useEffect(() => {
    if (!useApiMasters || !fieldMasterId) return
    void onLoadFieldValues(fieldMasterId)
  }, [useApiMasters, fieldMasterId, onLoadFieldValues])

  const legacyFieldDef = BENEFIT_FIELDS.find((f) => f.key === rule.field)
  const passAccent = ACTION_ACCENT[rule.action] ?? ACTION_ACCENT.continue
  const failAccent = ACTION_ACCENT[rule.failAction] ?? ACTION_ACCENT.flag

  const fieldOptions = useApiMasters
    ? fields.map((f) => ({ value: f.id, label: f.display_label }))
    : BENEFIT_FIELDS.map((f) => ({ value: f.key, label: f.label }))

  const operatorOptions = useApiMasters
    ? operators.map((o) => ({ value: o.id, label: o.display_label }))
    : RULE_OPERATORS.map((o) => ({ value: o.key, label: o.label }))

  const actionOptions = useApiMasters
    ? actions.map((a) => ({ value: a.id, label: a.display_label }))
    : RULE_ACTIONS.map((a) => ({ value: a.key, label: a.label }))

  const selectedFieldId = useApiMasters ? resolveFieldMasterId(rule, fields) : rule.field
  const selectedOperatorId = useApiMasters ? resolveOperatorMasterId(rule, operators) : rule.operator
  const selectedPassActionId = useApiMasters
    ? resolveActionMasterId(rule, actions, 'pass')
    : rule.action
  const selectedFailActionId = useApiMasters
    ? resolveActionMasterId(rule, actions, 'fail')
    : rule.failAction
  const selectedValueId = useApiMasters ? resolveValueMasterId(rule, fieldValues) : rule.value

  const showValueSelect =
    useApiMasters ? fieldValues.length > 0 : Boolean(legacyFieldDef?.type === 'select' && legacyFieldDef.options)

  return (
    <div
      className="rounded-2xl border shadow-sm transition-shadow hover:shadow-md"
      style={{
        borderColor: theme.colors.cardBorder,
        backgroundColor: theme.colors.cardBackground,
      }}
    >
      <div
        className="flex items-center gap-2 rounded-t-2xl border-b px-3 py-2.5"
        style={{ borderColor: theme.colors.border, backgroundColor: theme.colors.background }}
      >
        <div
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold text-white"
          style={{ backgroundColor: theme.colors.buttonPrimary }}
        >
          {index + 1}
        </div>
        <GripVertical className="h-4 w-4 shrink-0" style={{ color: theme.colors.textMuted }} />
        <Input
          name={`rule-name-${rule.id}`}
          value={rule.name}
          onChange={(e) => onUpdate({ ...rule, name: e.target.value })}
          placeholder="Rule name"
          className="flex-1 font-semibold"
        />
        <div className="flex shrink-0 items-center gap-1">
          <button
            type="button"
            onClick={() => onMove(index, -1)}
            disabled={index === 0}
            className="rounded-lg p-1.5 transition-colors disabled:opacity-30"
            style={{ color: theme.colors.textSecondary }}
            aria-label="Move rule up"
          >
            <ChevronUp className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => onMove(index, 1)}
            disabled={index === totalRules - 1}
            className="rounded-lg p-1.5 transition-colors disabled:opacity-30"
            style={{ color: theme.colors.textSecondary }}
            aria-label="Move rule down"
          >
            <ChevronDown className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={onDelete}
            className="rounded-lg p-1.5 transition-colors hover:bg-red-50"
            style={{ color: '#dc2626' }}
            aria-label="Delete rule"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="space-y-3 p-4">
        <div
          className="rounded-xl border p-3"
          style={{ borderColor: theme.colors.border, backgroundColor: theme.colors.background }}
        >
          <div className="mb-2 flex items-center gap-2">
            <span
              className="rounded-md px-2 py-0.5 text-[11px] font-bold tracking-wide text-white"
              style={{ backgroundColor: theme.colors.textSecondary }}
            >
              IF
            </span>
            <span className="text-xs font-medium" style={{ color: theme.colors.textMuted }}>
              Condition
            </span>
          </div>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
            <Select
              name={`rule-field-${rule.id}`}
              value={selectedFieldId}
              onChange={(e) => {
                if (useApiMasters) {
                  const selected = fields.find((f) => f.id === e.target.value)
                  onUpdate({
                    ...rule,
                    conditionFieldMasterId: selected?.id,
                    field: selected?.value_key ?? '',
                    conditionValueMasterId: undefined,
                    value: '',
                  })
                  return
                }
                onUpdate({ ...rule, field: e.target.value, value: '' })
              }}
              options={fieldOptions}
              placeholder=""
            />
            <Select
              name={`rule-operator-${rule.id}`}
              value={selectedOperatorId}
              onChange={(e) => {
                if (useApiMasters) {
                  const selected = operators.find((o) => o.id === e.target.value)
                  onUpdate({
                    ...rule,
                    conditionOperatorMasterId: selected?.id,
                    operator: (selected?.value_key ?? 'equals') as BenefitRule['operator'],
                  })
                  return
                }
                onUpdate({ ...rule, operator: e.target.value as BenefitRule['operator'] })
              }}
              options={operatorOptions}
              placeholder=""
            />
            {showValueSelect ? (
              <Select
                name={`rule-value-${rule.id}`}
                value={selectedValueId}
                onChange={(e) => {
                  if (useApiMasters) {
                    const selected = fieldValues.find((v) => v.id === e.target.value)
                    onUpdate({
                      ...rule,
                      conditionValueMasterId: selected?.id,
                      value: selected?.value_key ?? '',
                    })
                    return
                  }
                  onUpdate({ ...rule, value: e.target.value })
                }}
                options={
                  useApiMasters
                    ? fieldValues.map((v) => ({ value: v.id, label: v.display_label }))
                    : [
                        ...(legacyFieldDef?.options ?? []).map((o) => ({ value: o, label: o })),
                        { value: 'oopMax', label: 'OOP Max (field ref)' },
                      ]
                }
                placeholder=""
              />
            ) : (
              <Input
                name={`rule-value-${rule.id}`}
                value={rule.value}
                onChange={(e) =>
                  onUpdate({
                    ...rule,
                    value: e.target.value,
                    conditionValueMasterId: undefined,
                  })
                }
                placeholder="Value or field ref"
              />
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div
            className="rounded-xl border p-3"
            style={{ borderColor: passAccent.border, backgroundColor: passAccent.bg }}
          >
            <div className="mb-2 flex items-center gap-1.5 text-xs font-bold" style={{ color: passAccent.color }}>
              <span className="text-sm">✓</span> ON PASS
            </div>
            <Select
              name={`rule-pass-action-${rule.id}`}
              value={selectedPassActionId}
              onChange={(e) => {
                if (useApiMasters) {
                  const selected = actions.find((a) => a.id === e.target.value)
                  onUpdate({
                    ...rule,
                    onPassActionMasterId: selected?.id,
                    action: (selected?.value_key ?? 'continue') as BenefitRule['action'],
                  })
                  return
                }
                onUpdate({ ...rule, action: e.target.value as BenefitRule['action'] })
              }}
              options={actionOptions}
              placeholder=""
            />
          </div>
          <div
            className="rounded-xl border p-3"
            style={{ borderColor: failAccent.border, backgroundColor: failAccent.bg }}
          >
            <div className="mb-2 flex items-center gap-1.5 text-xs font-bold" style={{ color: failAccent.color }}>
              <span className="text-sm">✕</span> ON FAIL
            </div>
            <Select
              name={`rule-fail-action-${rule.id}`}
              value={selectedFailActionId}
              onChange={(e) => {
                if (useApiMasters) {
                  const selected = actions.find((a) => a.id === e.target.value)
                  onUpdate({
                    ...rule,
                    onFailActionMasterId: selected?.id,
                    failAction: (selected?.value_key ?? 'flag') as BenefitRule['failAction'],
                  })
                  return
                }
                onUpdate({ ...rule, failAction: e.target.value as BenefitRule['failAction'] })
              }}
              options={actionOptions}
              placeholder=""
            />
          </div>
        </div>

        <div className="relative">
          <AlertCircle
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2"
            style={{ color: theme.colors.textMuted }}
          />
          <Input
            name={`rule-fail-message-${rule.id}`}
            value={rule.failMessage}
            onChange={(e) => onUpdate({ ...rule, failMessage: e.target.value })}
            placeholder="Fail message / routing note shown when the rule fails"
            className="pl-9 text-sm"
          />
        </div>
      </div>
    </div>
  )
}
